import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';

const RegisterMonthlyLayer = () => {
    const [payments, setPayments] = useState([]);
    const [rentals, setRentals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // Add/Edit modal state
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingPaymentId, setEditingPaymentId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const [formData, setFormData] = useState({
        rental_id: '',
        payment_month: '',
        amount_paid: '',
        payment_date: '',
        notes: ''
    });

    useEffect(() => {
        fetchRentals();
        fetchPayments();
    }, []);

    const fetchRentals = async () => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const response = await fetch(`${apiUrl}/rentals`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch rentals: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setRentals(data.data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchPayments = async () => {
        try {
            setLoading(true);
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const response = await fetch(`${apiUrl}/rental-payments`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch payments: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setPayments(data.data);
            } else {
                throw new Error(data.message || 'Failed to fetch payments');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            rental_id: '',
            payment_month: '',
            amount_paid: '',
            payment_date: new Date().toISOString().split('T')[0],
            notes: ''
        });
        setValidationErrors({});
        setIsEditMode(false);
        setEditingPaymentId(null);
    };

    const handleOpenAddModal = () => {
        resetForm();
        setShowModal(true);
    };

    const handleOpenEditModal = (payment) => {
        setFormData({
            rental_id: payment.rental_id || '',
            payment_month: payment.payment_month || '',
            amount_paid: payment.amount_paid || '',
            payment_date: payment.payment_date || '',
            notes: payment.notes || ''
        });
        setEditingPaymentId(payment.id);
        setIsEditMode(true);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setIsSubmitting(true);
            setValidationErrors({});
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const url = isEditMode 
                ? `${apiUrl}/rental-payments/${editingPaymentId}`
                : `${apiUrl}/rental-payments`;

            const method = isEditMode ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.errors) {
                    setValidationErrors(data.errors);
                }
                throw new Error(data.message || `Failed to ${isEditMode ? 'update' : 'register'} payment`);
            }

            if (data.success) {
                handleCloseModal();
                fetchPayments();
                alert(data.message || `Payment ${isEditMode ? 'updated' : 'registered'} successfully!`);
            } else {
                throw new Error(data.message || `Failed to ${isEditMode ? 'update' : 'register'} payment`);
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (paymentId, month) => {
        if (!window.confirm(`Are you sure you want to delete the payment for "${month}"?`)) {
            return;
        }

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/rental-payments/${paymentId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                fetchPayments();
                alert('Payment deleted successfully!');
            } else {
                throw new Error(data.message || 'Failed to delete payment');
            }
        } catch (err) {
            console.error(err);
            alert(err instanceof Error ? err.message : 'Failed to delete payment');
        }
    };

    const filteredPayments = payments.filter(payment =>
        (payment.rental && payment.rental.tenant_names.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (payment.payment_month && payment.payment_month.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <div className='card h-100 p-0 radius-12'>
            <div className='card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between'>
                <div className='d-flex align-items-center flex-wrap gap-3'>
                    <span className='text-md fw-medium text-secondary-light mb-0'>Show</span>
                    <select className='form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px'>
                        <option value='10'>10</option>
                        <option value='25'>25</option>
                        <option value='50'>50</option>
                    </select>
                    <form className='navbar-search'>
                        <input
                            type='text'
                            className='bg-base h-40-px w-auto'
                            name='search'
                            placeholder='Search'
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Icon icon='ion:search-outline' className='icon' />
                    </form>
                </div>
                <button
                    type='button'
                    className='btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2'
                    onClick={handleOpenAddModal}
                >
                    <Icon
                        icon='ic:baseline-plus'
                        className='icon text-xl line-height-1'
                    />
                    Register Monthly Payment
                </button>
            </div>
            <div className='card-body p-24'>
                {error && (
                    <div className='alert alert-danger mb-3' role='alert'>
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className='text-center py-5'>
                        <div className='spinner-border text-primary-600' role='status'>
                            <span className='visually-hidden'>Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className='table-responsive scroll-sm'>
                        <table className='table bordered-table sm-table mb-0'>
                            <thead>
                                <tr>
                                    <th scope='col'>No</th>
                                    <th scope='col'>Tenant Name</th>
                                    <th scope='col'>Payment Month</th>
                                    <th scope='col'>Amount Paid</th>
                                    <th scope='col'>Payment Date</th>
                                    <th scope='col'>Notes</th>
                                    <th scope='col' className='text-center'>
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPayments.length === 0 ? (
                                    <tr>
                                        <td colSpan='7' className='text-center py-4'>
                                            No payments found.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPayments.map((payment, index) => (
                                        <tr key={payment.id}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <div className='d-flex align-items-center'>
                                                    <span className='text-md mb-0 fw-normal text-secondary-light'>
                                                        {payment.rental ? payment.rental.tenant_names : 'N/A'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>{payment.payment_month}</td>
                                            <td>${payment.amount_paid}</td>
                                            <td>{payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : 'N/A'}</td>
                                            <td>{payment.notes || '-'}</td>
                                            <td className='text-center'>
                                                <div className='d-flex align-items-center gap-10 justify-content-center'>
                                                    <button
                                                        type='button'
                                                        className='bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle'
                                                        onClick={() => handleOpenEditModal(payment)}
                                                    >
                                                        <Icon
                                                            icon='lucide:edit'
                                                            className='menu-icon'
                                                        />
                                                    </button>
                                                    <button
                                                        type='button'
                                                        className='remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle'
                                                        onClick={() => handleDelete(payment.id, payment.payment_month)}
                                                    >
                                                        <Icon
                                                            icon='fluent:delete-24-regular'
                                                            className='menu-icon'
                                                        />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className='modal fade show d-block' style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className='modal-dialog modal-dialog-centered'>
                        <div className='modal-content'>
                            <div className='modal-header'>
                                <h5 className='modal-title'>{isEditMode ? 'Edit Payment' : 'Register Monthly Payment'}</h5>
                                <button
                                    type='button'
                                    className='btn-close'
                                    onClick={handleCloseModal}
                                ></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className='modal-body'>
                                    {error && (
                                        <div className='alert alert-danger mb-3' role='alert'>
                                            {error}
                                        </div>
                                    )}
                                    <div className='row gy-3'>
                                        <div className='col-12'>
                                            <label className='form-label'>Select Renter <span className='text-danger'>*</span></label>
                                            <select
                                                name='rental_id'
                                                className={`form-select ${validationErrors.rental_id ? 'is-invalid' : ''}`}
                                                value={formData.rental_id}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value=''>Choose a renter...</option>
                                                {rentals.map(rental => (
                                                    <option key={rental.id} value={rental.id}>
                                                        {rental.tenant_names}
                                                    </option>
                                                ))}
                                            </select>
                                            {validationErrors.rental_id && (
                                                <div className='invalid-feedback d-block'>
                                                    {validationErrors.rental_id[0]}
                                                </div>
                                            )}
                                        </div>
                                        <div className='col-12'>
                                            <label className='form-label'>Select The Month <span className='text-danger'>*</span></label>
                                            <input
                                                type='month'
                                                name='payment_month'
                                                className={`form-control ${validationErrors.payment_month ? 'is-invalid' : ''}`}
                                                value={formData.payment_month}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            {validationErrors.payment_month && (
                                                <div className='invalid-feedback d-block'>
                                                    {validationErrors.payment_month[0]}
                                                </div>
                                            )}
                                        </div>
                                        <div className='col-12'>
                                            <label className='form-label'>Amount Paid <span className='text-danger'>*</span></label>
                                            <input
                                                type='number'
                                                step='0.01'
                                                name='amount_paid'
                                                className={`form-control ${validationErrors.amount_paid ? 'is-invalid' : ''}`}
                                                placeholder='Enter amount paid'
                                                value={formData.amount_paid}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            {validationErrors.amount_paid && (
                                                <div className='invalid-feedback d-block'>
                                                    {validationErrors.amount_paid[0]}
                                                </div>
                                            )}
                                        </div>
                                        <div className='col-12'>
                                            <label className='form-label'>Payment Date</label>
                                            <input
                                                type='date'
                                                name='payment_date'
                                                className='form-control'
                                                value={formData.payment_date}
                                                onChange={handleInputChange}
                                            />
                                        </div>
                                        <div className='col-12'>
                                            <label className='form-label'>Notes</label>
                                            <textarea
                                                name='notes'
                                                className='form-control'
                                                rows='3'
                                                placeholder='Enter any notes...'
                                                value={formData.notes}
                                                onChange={handleInputChange}
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                                <div className='modal-footer'>
                                    <button
                                        type='button'
                                        className='btn btn-secondary'
                                        onClick={handleCloseModal}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type='submit'
                                        className='btn btn-primary'
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Payment' : 'Register Payment')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegisterMonthlyLayer;

